import { NationBuilderBrief } from '__generated__/NationBuilderBrief';
import { NationBuilderBriefFields } from '__generated__/NationBuilderBriefFields';
import { Currency } from 'common/utils/currency';
import Attachments from 'projects/components/Attachments';
import { BudgetSummary } from 'projects/components/BriefFields/BudgetField';
import ContentSetting from 'projects/components/ContentSetting';
import { formatAndLinkify } from 'projects/components/FormatAndLinkify';
import { linkify } from 'projects/components/Linkify';
import SummaryCard from 'projects/components/summary/SummaryCard';
import SummaryCardItem from 'projects/components/summary/SummaryCardItem';
import { TimeframeSummary } from 'projects/components/TimeFrame';
import { trackEditBriefEvent, trackViewedBriefEvent } from 'projects/events';
import { routes } from 'projects/routes';
import React, { useEffect } from 'react';
import {
    nationBuilderAppType,
    nationBuilderMobileAppType,
    nationBuilderNeeds,
    nationBuilderOrganizationType,
    nationBuilderPoliticalGroup,
} from './enums';

export interface Props {
    brief: NationBuilderBrief;
}

function SummaryPage(props: Props) {
    const { brief } = props;
    const fields = brief.fields as NationBuilderBriefFields;

    useEffect(() => {
        trackViewedBriefEvent({
            id: brief.id,
            page: 'Summary',
        });
    }, []);

    function profileApproval() {
        return fields.profileApproval ? __('Yes') : __('No');
    }

    function handleEditClick(page: 'AboutYou' | 'Details' | 'TimeAndBudget') {
        trackEditBriefEvent({
            id: brief.id,
            page: page,
        });
    }

    function renderDetailsSection() {
        const needs = nationBuilderNeeds.find(n => n.value === fields.needs);
        const appType = nationBuilderAppType.find(at => at.value === fields.appType);
        const mobileAppType = nationBuilderMobileAppType.find(
            mat => mat.value === fields.mobileAppType
        );

        return (
            <SummaryCard
                heading={__('Tell us about your project')}
                onEditClick={() => handleEditClick('Details')}
                url={routes.brief(brief, 1)}
            >
                <SummaryCardItem title={__('Name your project')}>{fields.title}</SummaryCardItem>

                {needs && (
                    <SummaryCardItem title={__('What best describes your need?')}>
                        {needs.title}
                    </SummaryCardItem>
                )}

                {appType && (
                    <SummaryCardItem
                        title={__('Do you need need help with a web or mobile application?')}
                    >
                        {appType.title}
                    </SummaryCardItem>
                )}

                {mobileAppType && (
                    <SummaryCardItem title={__('Do you need help with iOS, Android or Both?')}>
                        {mobileAppType.title}
                    </SummaryCardItem>
                )}

                {fields.existingWebsite !== '' && (
                    <SummaryCardItem title={__('Link to existing website')}>
                        {formatAndLinkify(fields.existingWebsite)}
                    </SummaryCardItem>
                )}

                <SummaryCardItem title={__('Describe your project')}>
                    {formatAndLinkify(fields.description)}
                </SummaryCardItem>

                {fields.referenceFiles.length > 0 && (
                    <SummaryCardItem title={__('Add reference files')}>
                        {Attachments(fields.referenceFiles)}
                    </SummaryCardItem>
                )}

                {fields.inspirationFiles.length > 0 && (
                    <SummaryCardItem title={__('Add inspiration files')}>
                        {Attachments(fields.inspirationFiles)}
                    </SummaryCardItem>
                )}

                {fields.inspirationUrls.length > 0 && (
                    <SummaryCardItem title={__('Link to any inspiration on the Web')}>
                        {linkify(fields.inspirationUrls)}
                    </SummaryCardItem>
                )}
            </SummaryCard>
        );
    }

    function renderTimeAndBudgetSection() {
        return (
            <SummaryCard
                heading={__('Your time & budget')}
                onEditClick={() => handleEditClick('TimeAndBudget')}
                url={routes.brief(brief, 2)}
            >
                <SummaryCardItem title={__("What's your timeline?")}>
                    <TimeframeSummary
                        value={fields.timeframe}
                        startDate={fields.startDate}
                        endDate={fields.endDate}
                    />
                </SummaryCardItem>

                <SummaryCardItem title={__("What's your budget?")}>
                    <BudgetSummary budget={fields.budget} currency={Currency.USD} />
                </SummaryCardItem>
            </SummaryCard>
        );
    }

    function renderAboutYouSection() {
        const { brief } = props;
        const organizationType = nationBuilderOrganizationType.find(
            ot => ot.value === fields.organizationType
        );
        const politicalGroup = nationBuilderPoliticalGroup.find(
            pg => pg.value === fields.politicalGroup
        );

        return (
            <SummaryCard
                heading={__('About you')}
                onEditClick={() => handleEditClick('AboutYou')}
                url={routes.brief(brief, 3)}
            >
                {organizationType && (
                    <SummaryCardItem title={__('What best describes your organization type?')}>
                        {organizationType.title}
                    </SummaryCardItem>
                )}

                {politicalGroup && (
                    <SummaryCardItem title={__('Which group does your organization identify as?')}>
                        {politicalGroup.title}
                    </SummaryCardItem>
                )}

                <SummaryCardItem
                    title={__(
                        'Can your designer showcase the finished designs in their 99designs portfolio?'
                    )}
                >
                    {profileApproval()}
                </SummaryCardItem>

                {fields.other != '' && (
                    <SummaryCardItem
                        title={__("Anything else you'd like to share with your designer?")}
                    >
                        {formatAndLinkify(fields.other)}
                    </SummaryCardItem>
                )}
            </SummaryCard>
        );
    }

    return (
        <ContentSetting>
            {renderDetailsSection()}
            {renderTimeAndBudgetSection()}
            {renderAboutYouSection()}
        </ContentSetting>
    );
}

export default SummaryPage;
